"use client"

import { useState, useEffect } from "react"
import { Menu } from "@headlessui/react"
import {
  MagnifyingGlass,
  DotsThreeOutlineVertical,
  WarningCircle,
  SpinnerGap,
  FunnelSimple,
  X,
  PlusCircle,
  ArrowClockwise,
  DownloadSimple,
  ArrowRight,
  CaretLeft,
  CaretRight,
  CaretDoubleLeft,
  CaretDoubleRight,
  Eye,
  PencilSimple,
  Trash,
  CheckCircle,
  Clock,
  ChatCircleDots,
} from "phosphor-react"

import { SearchableSelect, DeleteModal } from "../../components"
import CreateTicketModal from "./CreateTicketModal"
import TicketDetailsModal from "./TicketDetailsModal"
import UpdateTicketStatusModal from "./UpdateTicketStatusModal"
import ticketsService from "../../services/ticketsService"
import { useCurrentUser } from "../../utils/context/UserProvider"
import { useAccessChecker } from "../../utils/Functions/Authorization"

const TicketsPage = () => {
  const { currentUser } = useCurrentUser()
  const { checkAccess } = useAccessChecker()

  // State for data
  const [tickets, setTickets] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingOptions, setLoadingOptions] = useState(false)

  // State for filters
  const [searchQuery, setSearchQuery] = useState("")
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [filters, setFilters] = useState({
    status: "",
    priority: "",
    reason: "",
  })

  // State for modals
  const [dropdownOpen, setDropdownOpen] = useState(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [ticketToDelete, setTicketToDelete] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false)
  const [selectedTicket, setSelectedTicket] = useState(null)

  // State for pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 5,
    totalPages: 1,
    totalItems: 0,
  })

  // Track if filters are active
  const [filtersActive, setFiltersActive] = useState(false)
  const [done, setDone] = useState(false)

  // Kept to not touch your logic shape
  const [roleId, setRoleId] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  // Permissions
  const canUpdateStatus =
    currentUser?.role?.name !== "Client" && checkAccess("tickets", "update")

  // Set page title
  useEffect(() => {
    const event = new CustomEvent("setPageTitle", {
      detail: { title: "Support Tickets" },
    })
    window.dispatchEvent(event)

    return () => {
      const resetEvent = new CustomEvent("setPageTitle", {
        detail: { title: "Dashboard" },
      })
      window.dispatchEvent(resetEvent)
    }
  }, [])

  // Track active filters
  useEffect(() => {
    const hasActiveFilters =
      searchQuery !== "" ||
      filters.status !== "" ||
      filters.priority !== "" ||
      filters.reason !== ""

    setFiltersActive(hasActiveFilters)
  }, [searchQuery, filters])

  // Fetch tickets + stats
  useEffect(() => {
    fetchTickets()
    fetchStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, pagination.currentPage, pagination.pageSize])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest(".ticket-dropdown")) {
        setDropdownOpen(null)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [dropdownOpen])

  /* ────────────────────────────── API Calls ────────────────────────────── */

  const fetchTickets = async () => {
    try {
      setLoading(true)

      const payload = {
        ...filters,
        searchValue: searchQuery,
        pagination: {
          page: pagination.currentPage,
          limit: pagination.pageSize,
        },
      }

      const response = await ticketsService.search(payload)
      const responseData = response.data

      if (responseData && responseData.statusCode === 200) {
        setTickets(responseData.data)

        if (responseData.pagination) {
          setPagination({
            currentPage: responseData.pagination.currentPage,
            pageSize: responseData.pagination.pageSize,
            totalPages: responseData.pagination.totalPages,
            totalItems: responseData.pagination.totalItems,
          })
        }
      }
      setLoading(false)
    } catch (error) {
      console.error("Failed to fetch tickets:", error)
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await ticketsService.getStats()
      const responseData = response.data

      if (responseData && responseData.statusCode === 200) {
        setStats(responseData.data)
      }
    } catch (error) {
      console.error("Error fetching stats:", error)
    }
  }

  /* ────────────────────────────── Filters ────────────────────────────── */

  const handleFilterChange = (filterName, value) => {
    setFilters({
      ...filters,
      [filterName]: value,
    })
  }

  const clearFilters = () => {
    setFilters({
      status: "",
      priority: "",
      reason: "",
    })
    setSearchQuery("")
  }

  const applyFilters = () => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }))
    fetchTickets()
  }

  const resetAndRefresh = () => {
    clearFilters()
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }))

    setTimeout(() => {
      fetchTickets()
    }, 0)
  }

  /* ────────────────────────────── Actions ────────────────────────────── */

  const toggleDropdown = (id) => {
    setDropdownOpen(dropdownOpen === id ? null : id)
  }

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket)
    setShowDetailsModal(true)
    setDropdownOpen(null)
  }

  const handleUpdateStatus = (ticket) => {
    setSelectedTicket(ticket)
    setShowUpdateStatusModal(true)
    setDropdownOpen(null)
  }

  const handleDeleteTicket = (ticketId) => {
    setTicketToDelete(ticketId)
    setIsDeleteModalOpen(true)
    setDropdownOpen(null)
  }

  const handleExport = () => {
    console.log("Exporting data")
  }

  const handleCreate = () => {
    setShowCreateModal(true)
  }

  const handleFormSuccess = () => {
    setDone((prev) => !prev)
  }

  const closeDeleteModal = () => {
    setTicketToDelete(null)
    setIsDeleteModalOpen(false)
  }

  const onClose = () => {
    setShowCreateModal(false)
    setShowDetailsModal(false)
    setShowUpdateStatusModal(false)
    setTimeout(() => {
      setSelectedTicket(null)
    }, 500)
  }

  const deleteTicket = async () => {
    try {
      const response = await ticketsService.delete(ticketToDelete)

      if (response.data && response.data.statusCode === 200) {
        setTickets(tickets.filter((ticket) => ticket._id !== ticketToDelete))
        console.log("Ticket deleted successfully")
      } else {
        console.error("Error deleting ticket:", response.status)
      }
    } catch (error) {
      console.error("Error deleting ticket:", error)
    } finally {
      closeDeleteModal()
    }
  }

  const handleSearch = () => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }))
    fetchTickets()
  }

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return

    setPagination((prev) => ({
      ...prev,
      currentPage: newPage,
    }))
  }

  const handlePageSizeChange = (e) => {
    const newSize = Number.parseInt(e.target.value)
    setPagination((prev) => ({
      ...prev,
      pageSize: newSize,
      currentPage: 1,
    }))
  }

  /* ────────────────────────────── Helpers ────────────────────────────── */

  const formatDate = (raw) => {
    if (!raw) return "N/A";
    const dateString = String(raw).trim();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const dotDateRE =
      /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/;
    const hasExplicitTime =
      /T\d{2}:\d{2}/.test(dateString) || /\s\d{1,2}:\d{2}/.test(dateString);

    let d;
    const m = dateString.match(dotDateRE);
    if (m) {
      const [, dd, mm, yyyy, HH, MM] = m;
      d = new Date(
        parseInt(yyyy, 10),
        parseInt(mm, 10) - 1,
        parseInt(dd, 10),
        HH ? parseInt(HH, 10) : 0,
        MM ? parseInt(MM, 10) : 0,
        0,
        0
      );
    } else {
      d = new Date(dateString);
    }
    if (isNaN(d.getTime())) return raw;

    const datePart = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone,
    }).format(d);

    if (!hasExplicitTime) return datePart;

    const timeFormatter = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone,
    });

    const parts = timeFormatter.formatToParts(d);
    const hour = parts.find((p) => p.type === "hour")?.value ?? "00";
    const minute = parts.find((p) => p.type === "minute")?.value ?? "00";

    const hourNum = parseInt(hour, 10);
    const suffix = hourNum < 12 ? "AM" : "PM";

    const timePart = `${hour}:${minute} ${suffix}`;

    return `${datePart}, ${timePart}`;
  };
  const getStatusColor = (status) => {
    const colorMap = {
      open: "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300",
      in_progress: "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300",
      waiting_client: "bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300",
      waiting_admin: "bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300",
      resolved: "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300",
      closed: "bg-neutral-50 text-neutral-700 dark:bg-neutral-900/20 dark:text-neutral-300",
    }
    return (
      colorMap[status] ||
      "bg-neutral-50 text-neutral-700 dark:bg-neutral-900/20 dark:text-neutral-300"
    )
  }

  const getPriorityColor = (priority) => {
    const colorMap = {
      low: "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300",
      medium: "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300",
      high: "bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300",
      urgent: "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300",
    }
    return (
      colorMap[priority] ||
      "bg-neutral-50 text-neutral-700 dark:bg-neutral-900/20 dark:text-neutral-300"
    )
  }

  const getStatsCount = (statsObj, key) => {
    return statsObj?.find((stat) => stat._id === key)?.count || 0
  }

  /* ────────────────────────────── Render ────────────────────────────── */

  return (
    <div className="lg:p-7 p-3 max-w-full mx-auto">
      {/* Top Bar: Search / Filters / Export / Create */}
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        {/* Search input */}
        <div className="relative flex-grow max-w-xs">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <MagnifyingGlass size={16} className="text-neutral-800 dark:text-neutral-400" />
          </div>
          <input
            type="text"
            placeholder="Search tickets..."
            className="h-10 w-full border pl-10 pr-10 text-sm border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl placeholder-neutral-500 cursor-pointer ring-neutral-300 dark:ring-neutral-700 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200 focus:outline-none focus:ring-1"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch()
            }}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
            onClick={handleSearch}
            aria-label="Submit search"
          >
            <ArrowRight size={16} />
          </button>
        </div>

        {/* More filters (yellow accent like BalanceRecords) */}
        <button
          className={`flex items-center ring-yellow-500/50 gap-2 px-4 py-2.5 h-10 w-fit border text-sm rounded-xl cursor-pointer focus:outline-none focus:ring-1 ${
            showMoreFilters
              ? "border-yellow-500/50 bg-yellow-50 text-yellow-600 dark:border-yellow-600/30 dark:bg-yellow-900/20 dark:text-yellow-400"
              : "border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200"
          }`}
          onClick={() => setShowMoreFilters(!showMoreFilters)}
        >
          <span className="text-sm">
            {showMoreFilters ? "Hide filters" : "More filters"}
          </span>
          {showMoreFilters ? (
            <X size={16} className="text-yellow-600 dark:text-yellow-400" />
          ) : (
            <PlusCircle size={16} className="text-neutral-800 dark:text-neutral-400" />
          )}
        </button>

        {/* Reset */}
        <button
          className="flex items-center gap-2 h-10 px-4 border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800"
          onClick={resetAndRefresh}
          aria-label="Reset filters"
        >
          <ArrowClockwise size={16} className="text-neutral-800 dark:text-neutral-400" />
          <span className="text-sm">Reset</span>
        </button>

        {/* Apply filters */}
        {filtersActive && !showMoreFilters && (
          <button
            className="flex items-center text-sm justify-center px-4 gap-2 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300"
            onClick={applyFilters}
          >
            <FunnelSimple size={16} />
            <span className="text-sm">Apply Filters</span>
          </button>
        )}

        {/* Export */}
        <button
          className="flex items-center justify-center h-10 w-10 border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800"
          onClick={handleExport}
        >
          <DownloadSimple size={16} className="text-neutral-800 dark:text-neutral-400" />
        </button>

        {/* Create — server only allows Client accounts to open tickets */}
        {currentUser?.role?.name === "Client" && (
          <button
            className="flex items-center gap-2 px-4 h-10 bg-neutral-900 hover:bg-neutral-950 dark:bg-neutral-100 dark:hover:bg-neutral-200 text-white dark:text-black rounded-xl hover:scale-105 transition-all duration-300 ml-auto"
            onClick={handleCreate}
          >
            <PlusCircle size={16} />
            <span className="text-sm">Create Ticket</span>
          </button>
        )}
      </div>

      {/* Active filter chips */}
      {filtersActive && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            Active filters:
          </span>

          {searchQuery && (
            <Chip
              icon={<MagnifyingGlass size={12} />}
              label={searchQuery}
              onClear={() => {
                setSearchQuery("")
                handleSearch()
              }}
            />
          )}

          {filters.status && (
            <Chip
              icon={<WarningCircle size={12} />}
              label={filters.status}
              onClear={() => {
                handleFilterChange("status", "")
                applyFilters()
              }}
            />
          )}

          {filters.priority && (
            <Chip
              icon={<Clock size={12} />}
              label={filters.priority}
              onClear={() => {
                handleFilterChange("priority", "")
                applyFilters()
              }}
            />
          )}

          {filters.reason && (
            <Chip
              icon={<ChatCircleDots size={12} />}
              label={filters.reason}
              onClear={() => {
                handleFilterChange("reason", "")
                applyFilters()
              }}
            />
          )}

          <button
            onClick={resetAndRefresh}
            className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1 ml-2"
          >
            <X size={12} />
            <span>Clear all</span>
          </button>
        </div>
      )}

      {/* Expanded filters */}
      {showMoreFilters && (
        <div className="mb-6 p-6 border border-neutral-150 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-[300] text-neutral-800 dark:text-white">
              More filters
            </h2>
            <button
              onClick={clearFilters}
              className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1.5"
            >
              <X size={14} />
              <span>Clear all</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <SearchableSelect
              options={[
                { id: "all", value: "", name: "All Status" },
                { id: "open", value: "open", name: "Open" },
                { id: "in_progress", value: "in_progress", name: "In Progress" },
                { id: "waiting_client", value: "waiting_client", name: "Waiting Client" },
                { id: "waiting_admin", value: "waiting_admin", name: "Waiting Admin" },
                { id: "resolved", value: "resolved", name: "Resolved" },
                { id: "closed", value: "closed", name: "Closed" },
              ]}
              value={filters.status}
              onChange={(value) => handleFilterChange("status", value)}
              placeholder="Status"
              icon={<WarningCircle size={16} />}
              loading={loadingOptions}
            />

            <SearchableSelect
              options={[
                { id: "all", value: "", name: "All Priority" },
                { id: "low", value: "low", name: "Low" },
                { id: "medium", value: "medium", name: "Medium" },
                { id: "high", value: "high", name: "High" },
                { id: "urgent", value: "urgent", name: "Urgent" },
              ]}
              value={filters.priority}
              onChange={(value) => handleFilterChange("priority", value)}
              placeholder="Priority"
              icon={<Clock size={16} />}
              loading={loadingOptions}
            />

            <SearchableSelect
              options={[
                { id: "all", value: "", name: "All Reasons" },
                { id: "account_suspended", value: "account_suspended", name: "Account Suspended" },
                { id: "payment_issue", value: "payment_issue", name: "Payment Issue" },
                { id: "ad_rejected", value: "ad_rejected", name: "Ad Rejected" },
                { id: "billing_problem", value: "billing_problem", name: "Billing Problem" },
                { id: "technical_issue", value: "technical_issue", name: "Technical Issue" },
                { id: "account_access", value: "account_access", name: "Account Access" },
                { id: "policy_violation", value: "policy_violation", name: "Policy Violation" },
                { id: "performance_issue", value: "performance_issue", name: "Performance Issue" },
                { id: "other", value: "other", name: "Other" },
              ]}
              value={filters.reason}
              onChange={(value) => handleFilterChange("reason", value)}
              placeholder="Reason"
              icon={<ChatCircleDots size={16} />}
              loading={loadingOptions}
            />

            <div className="flex items-center gap-2">
              <button
                className="flex items-center justify-center px-4 gap-2 h-9 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300"
                onClick={applyFilters}
              >
                <FunnelSimple size={16} />
                <span className="text-sm">Apply Filters</span>
              </button>
              <button
                className="flex items-center justify-center px-4 gap-2 h-9 border border-neutral-200 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700"
                onClick={resetAndRefresh}
              >
                <ArrowClockwise size={16} />
                <span className="text-sm">Reset</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-neutral-950/80 border border-neutral-150 dark:border-neutral-900 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Open Tickets
                </p>
                <p className="text-2xl text-start font-[500] text-neutral-900 dark:text-white">
                  {getStatsCount(stats.statusStats, "open")}
                </p>
              </div>
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                <WarningCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-neutral-950/80 border border-neutral-150 dark:border-neutral-900 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  In Progress
                </p>
                <p className="text-2xl text-start font-[500] text-neutral-900 dark:text-white">
                  {getStatsCount(stats.statusStats, "in_progress")}
                </p>
              </div>
              <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl">
                <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-neutral-950/80 border border-neutral-150 dark:border-neutral-900 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Resolved
                </p>
                <p className="text-2xl text-start font-[500] text-neutral-900 dark:text-white">
                  {getStatsCount(stats.statusStats, "resolved")}
                </p>
              </div>
              <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-xl">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-neutral-950/80 border border-neutral-150 dark:border-neutral-900 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  High Priority
                </p>
                <p className="text-2xl text-start font-[500] text-neutral-900 dark:text-white">
                  {getStatsCount(stats.priorityStats, "high") +
                    getStatsCount(stats.priorityStats, "urgent")}
                </p>
              </div>
              <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-xl">
                <WarningCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tickets table */}
      <div className="border border-neutral-150 dark:border-neutral-900 rounded-lg bg-white dark:bg-neutral-950">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <SpinnerGap
              size={30}
              weight="bold"
              className="animate-spin text-neutral-600 dark:text-neutral-400"
            />
          </div>
        ) : tickets.length > 0 ? (
          <>
            <div className="overflow-x-auto rounded-t-lg">
              <table className="min-w-full divide-y divide-neutral-150 dark:divide-neutral-800">
                <thead className="bg-neutral-50/80 backdrop-blur-sm dark:bg-neutral-900/40">
                  <tr>
                    <Th>Ticket</Th>
                    <Th>Status</Th>
                    <Th>Priority</Th>
                    <Th>Ad Account</Th>
                    <Th>Client</Th>
                    <Th>Created</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-neutral-950 divide-y divide-neutral-100 dark:divide-neutral-800">
                  {tickets.map((ticket) => (
                    <tr
                      key={ticket._id}
                      className="group hover:bg-neutral-50/80 dark:hover:bg-neutral-900/60 transition-colors"
                    >
                      <Td>
                        <div className="flex items-center space-x-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-[400] text-neutral-500 dark:text-neutral-400 truncate">
                              {ticket.ticket_id}
                            </div>
                            <div className="text-sm font-[400] text-neutral-900 dark:text-white truncate">
                              {ticket.title}
                            </div>
                            {ticket.description && (
                              <div className="text-xs text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
                                {ticket.description.substring(0, 50)}...
                              </div>
                            )}
                          </div>
                        </div>
                      </Td>

                      <Td>
                        <span
                          className={`inline-flex whitespace-nowrap items-center px-4 py-1 capitalize rounded-full text-xs font-medium ${getStatusColor(
                            ticket.status
                          )}`}
                        >
                          {ticket.status.replace("_", " ")}
                        </span>
                      </Td>

                      <Td>
                        <span
                          className={`inline-flex whitespace-nowrap items-center px-4 py-1 capitalize rounded-full text-xs font-medium ${getPriorityColor(
                            ticket.priority
                          )}`}
                        >
                          {ticket.priority}
                        </span>
                      </Td>

                      <Td>
                        <div className="text-sm whitespace-nowrap text-neutral-900 dark:text-white">
                          {ticket.ad_account?.account_name || "N/A"}
                        </div>
                        {ticket.ad_account?.platform && (
                          <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                            {ticket.ad_account.platform}
                          </div>
                        )}
                      </Td>

                      <Td>
                        <div className="text-sm whitespace-nowrap text-neutral-900 dark:text-white">
                          {ticket.client?.reference || ticket.client?.fullName}
                        </div>
                        {ticket.client?.email && (
                          <div className="text-xs whitespace-nowrap text-neutral-500 dark:text-neutral-400 mt-0.5 truncate max-w-[180px]">
                            {ticket.client.email}
                          </div>
                        )}
                      </Td>

                      <Td>
                        <div className="text-sm text-neutral-900 dark:text:white">
                          {formatDate(ticket.createdAt)}
                        </div>
                      </Td>

                      <Td className="text-sm font-[300]">
                        <div className="ticket-dropdown relative flex justify-start">
                          <Menu as="div" className="relative">
                            <Menu.Button className="opacity-60 group-hover:opacity-100 p-1.5 rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500 dark:text-neutral-400 transition-all duration-150">
                              <DotsThreeOutlineVertical size={18} weight="bold" />
                            </Menu.Button>

                            <Menu.Items className="absolute right-0 top-full mt-1 w-44 bg-white dark:bg-neutral-950 rounded-xl shadow-lg border border-neutral-200 dark:border-neutral-800 py-1 z-[100] focus:outline-none">
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => handleViewTicket(ticket)}
                                    className={`flex items-center w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                                      active
                                        ? "bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-white"
                                        : "text-neutral-700 dark:text-neutral-300"
                                    }`}
                                  >
                                    <Eye size={14} className="mr-2" />
                                    View Details
                                  </button>
                                )}
                              </Menu.Item>

                              {canUpdateStatus && (
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      onClick={() => handleUpdateStatus(ticket)}
                                      className={`flex items-center w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                                        active
                                          ? "bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-white"
                                          : "text-neutral-700 dark:text-neutral-300"
                                      }`}
                                    >
                                      <PencilSimple size={14} className="mr-2" />
                                      Update Status
                                    </button>
                                  )}
                                </Menu.Item>
                              )}

                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => handleDeleteTicket(ticket._id)}
                                    className={`flex items-center w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                                      active
                                        ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400"
                                        : "text-red-600 dark:text-red-400"
                                    }`}
                                  >
                                    <Trash size={14} className="mr-2" />
                                    Delete
                                  </button>
                                )}
                              </Menu.Item>
                            </Menu.Items>
                          </Menu>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-neutral-150 dark:border-neutral-900 bg-neutral-50/60 dark:bg-neutral-900/40 rounded-b-lg">
              <div className="flex items-center mb-4 sm:mb-0">
                <span className="text-[13px] text-neutral-600 dark:text-neutral-400">
                  Showing {tickets.length} of {pagination.totalItems} tickets
                </span>

                <div className="ml-4 flex items-center gap-2">
                  <span className="text-[12px] text-neutral-500 dark:text-neutral-400">
                    Rows
                  </span>
                  <div className="relative">
                    <select
                      value={pagination.pageSize}
                      onChange={handlePageSizeChange}
                      className="h-8 pl-3 pr-8 text-[12px] border border-neutral-200/80 dark:border-neutral-800 rounded-xl bg-white dark:bg-neutral-950/80 text-neutral-800 dark:text-neutral-100 shadow-sm focus:outline-none focus:ring-1 focus:ring-neutral-300 dark:focus:ring-neutral-600 appearance-none cursor-pointer"
                    >
                    <option value={5}>5 / page</option>

                      <option value={10}>10 / page</option>
                      <option value={25}>25 / page</option>
                      <option value={50}>50 / page</option>
                      <option value={100}>100 / page</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <PageBtn
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(1)}
                  aria="First page"
                >
                  <CaretDoubleLeft size={14} />
                </PageBtn>
                <PageBtn
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  aria="Previous page"
                >
                  <CaretLeft size={14} />
                </PageBtn>

                <div className="flex items-center space-x-1">
                  {getPageNumbers(pagination.currentPage, pagination.totalPages).map(
                    (page, idx) =>
                      page === "..." ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="px-2 py-1 text-neutral-500 dark:text-neutral-400"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`w-7 h-7 flex items-center justify-center rounded-xl text-[13px] ${
                            pagination.currentPage === page
                              ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black"
                              : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                          }`}
                        >
                          {page}
                        </button>
                      )
                  )}
                </div>

                <PageBtn
                  disabled={
                    pagination.currentPage === pagination.totalPages ||
                    pagination.totalPages === 0
                  }
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  aria="Next page"
                >
                  <CaretRight size={14} />
                </PageBtn>
                <PageBtn
                  disabled={
                    pagination.currentPage === pagination.totalPages ||
                    pagination.totalPages === 0
                  }
                  onClick={() => handlePageChange(pagination.totalPages)}
                  aria="Last page"
                >
                  <CaretDoubleRight size={14} />
                </PageBtn>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col text-sm items-center justify-center h-64 p-8">
            <div className="p-2 rounded-full bg-neutral-100 dark:bg-neutral-900/20 flex items-center justify-center mb-4">
              <WarningCircle size={24} className="text-neutral-600 dark:text-neutral-400" />
            </div>
            <h3 className="text-xl font-[400] text-neutral-900 dark:text-white mb-2">
              No Tickets Found
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-md mb-6">
              {searchQuery || Object.values(filters).some(Boolean)
                ? "No tickets match your search criteria. Try adjusting your filters or search term."
                : "There are no tickets in the system yet. Create your first ticket to get started."}
            </p>
            <div className="flex gap-4">
              <button
                onClick={resetAndRefresh}
                className="px-4 py-2 border border-neutral-200 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2"
              >
                <ArrowClockwise size={16} />
                Reset Filters
              </button>

              {currentUser?.role?.name === "Client" && (
                <button
                  onClick={handleCreate}
                  className="px-4 py-2 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300 flex items-center gap-2"
                >
                  <PlusCircle size={16} />
                  <span>Create Ticket</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateTicketModal
        isOpen={showCreateModal}
        onClose={onClose}
        onSuccess={handleFormSuccess}
      />

      {selectedTicket && (
        <TicketDetailsModal
          isOpen={showDetailsModal}
          onClose={onClose}
          ticket={selectedTicket}
          onTicketUpdated={handleFormSuccess}
        />
      )}

      {selectedTicket && (
        <UpdateTicketStatusModal
          isOpen={showUpdateStatusModal}
          onClose={onClose}
          ticket={selectedTicket}
          onSuccess={handleFormSuccess}
        />
      )}

      <DeleteModal
        setDeleteId={setTicketToDelete}
        open={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        DeleteId={ticketToDelete}
        Model="tickets"
        setDone={setDone}
        setLoding={setLoading}
        done={done}
      />
    </div>
  )
}

/* ───────────────────── Small UI helpers ───────────────────── */

function Th({ children }) {
  return (
    <th className="px-6 py-5 text-left text-[12px] font-[400] text-neutral-500 dark:text-neutral-400 uppercase tracking-[0.08em] whitespace-nowrap">
      {children}
    </th>
  )
}

function Td({ children, className = "" }) {
  return (
    <td
      className={`px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-200 ${className}`}
    >
      {children}
    </td>
  )
}

function Chip({ label, onClear, icon }) {
  return (
    <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-sm">
      {icon}
      <span>{label}</span>
      <button onClick={onClear} className="ml-1">
        <X size={12} />
      </button>
    </div>
  )
}

function PageBtn({ disabled, onClick, children, aria }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`p-1 rounded-xl ${
        disabled
          ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
          : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
      }`}
      aria-label={aria}
    >
      {children}
    </button>
  )
}

function getPageNumbers(currentPage, totalPages) {
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1)
  if (currentPage <= 3) return [1, 2, 3, 4, "...", totalPages]
  if (currentPage >= totalPages - 2)
    return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages]
}

export default TicketsPage
